import type { InvalidateQueryFilters, Query, QueryClient } from '@tanstack/react-query';
import type { AssetIdPayload, AssetUpdatedPayload, AssetVersionActivatedPayload } from '@/hooks/websocket/types';
import type { Asset, GetAssetsRoot } from '@/types/asset';
import { QueryKey } from '@/lib/queryKeys';

export const mergeAssetPayloadIntoSingleCache = (queryClient: QueryClient, update: AssetUpdatedPayload): void => {
  queryClient.setQueryData<Asset>([QueryKey.Asset, update.id], (old) => (old ? ({ ...old, ...update } as Asset) : old));
};

const listCacheHoldsAsset = (data: unknown, assetId: string): boolean => {
  const assets = (data as GetAssetsRoot | undefined)?.assets?.assets;
  return Array.isArray(assets) && assets.some((asset) => asset.id === assetId);
};

/**
 * Whether a remote removal is worth prompting the viewer to refresh for.
 *
 * True when a list they are looking at still shows the asset and no refetch is
 * already on the way. The caches are deliberately left alone: rewriting them
 * here made rows vanish under the pointer, and it could not pull up the row
 * that should take the removed one's place from the next page anyway. Nothing
 * changes on screen until the viewer takes the refresh.
 *
 * A query already fetching is refetching because this client is the one
 * that removed the asset — it heals itself, so no prompt. But "fetching" alone
 * cannot tell a removal-caused refetch apart from an unrelated one (a sibling
 * folder rename, create, or undelete) that happens to overlap it. Those are
 * tagged via `invalidateAssetDataForNodeChange` below and excluded here, so an
 * unrelated in-flight refetch can't swallow a genuine removal prompt.
 */
const nodeChangeRefetchingQueries = new WeakSet<Query>();

export const invalidateAssetDataForNodeChange = (
  queryClient: QueryClient,
  filters?: InvalidateQueryFilters,
): Promise<void> => {
  const queries = queryClient
    .getQueryCache()
    .findAll({ queryKey: [QueryKey.AssetData] })
    .filter((query) => query.state.fetchStatus !== 'fetching');
  queries.forEach((query) => nodeChangeRefetchingQueries.add(query));

  return queryClient
    .invalidateQueries({ queryKey: [QueryKey.AssetData], ...filters })
    .finally(() => queries.forEach((query) => nodeChangeRefetchingQueries.delete(query)));
};

export const removalNeedsRefresh = (queryClient: QueryClient, payload: AssetIdPayload): boolean =>
  queryClient
    .getQueryCache()
    .findAll({ queryKey: [QueryKey.AssetData] })
    .some(
      (query) =>
        listCacheHoldsAsset(query.state.data, payload.id) &&
        (query.state.fetchStatus !== 'fetching' || nodeChangeRefetchingQueries.has(query)),
    );

/**
 * Flips the trashed state on the single-asset cache, which is what the inspector reads.
 *
 * The list stays untouched - the row must not move under the viewer - but an inspector
 * that still offers editable fields for an asset now in the bin is lying, and every save
 * it accepts will fail.
 *
 * Only `trashed` is written. The broadcast carries an id and nothing else, so there is no
 * honest value for `trashedAt`; inventing one would put the moment the viewer was told
 * where the moment it was trashed belongs. The refresh brings the real one.
 */
export const setAssetTrashedInSingleCache = (queryClient: QueryClient, assetId: string, trashed: boolean): void => {
  queryClient.setQueryData<Asset>([QueryKey.Asset, assetId], (old) => (old ? ({ ...old, trashed } as Asset) : old));
};

/**
 * Marks an asset's state in place, everywhere it is on screen.
 *
 * The row keeps its position and the pager keeps its count - removing it would move
 * everything below it under the viewer, which is what this whole design avoids. It is
 * annotated instead, so the gallery can show what happened to it: one that merely left
 * this list greys but stays selectable, a deleted one greys further and stops responding.
 * The refresh is what finally takes it away.
 */
export const markAssetInListCaches = (
  queryClient: QueryClient,
  assetId: string,
  patch: { departed?: 'trashed' | 'restored' | undefined; deleted?: boolean },
): void => {
  queryClient.setQueriesData<GetAssetsRoot>({ queryKey: [QueryKey.AssetData] }, (oldData) => {
    if (!oldData?.assets?.assets) {
      return oldData;
    }

    const needsPatch = oldData.assets.assets.some(
      (asset) =>
        asset.id === assetId && Object.entries(patch).some(([key, value]) => asset[key as keyof Asset] !== value),
    );

    if (!needsPatch) {
      return oldData;
    }

    return {
      ...oldData,
      assets: {
        ...oldData.assets,
        assets: oldData.assets.assets.map((asset) =>
          asset.id === assetId ? ({ ...asset, ...patch } as Asset) : asset,
        ),
      },
    };
  });
};

export const mergeAssetPayloadIntoListCaches = (queryClient: QueryClient, update: AssetUpdatedPayload): void => {
  queryClient.setQueriesData<GetAssetsRoot>({ queryKey: [QueryKey.AssetData] }, (oldData) => {
    if (!oldData?.assets?.assets) {
      return oldData;
    }
    return {
      ...oldData,
      assets: {
        ...oldData.assets,
        assets: oldData.assets.assets.map((asset) =>
          asset.id === update.id ? ({ ...asset, ...update } as Asset) : asset,
        ),
      },
    };
  });
};

const mergeVariantsByKey = (
  current: Asset['variants'] | undefined,
  incoming: AssetVersionActivatedPayload['variants'],
): Asset['variants'] => {
  const base = Array.isArray(current) || !current ? {} : current;
  return { ...base, ...incoming };
};

// Same event name, two payload shapes (variants-only or type-only); only touch a field the payload actually carries.
const applyVersionActivated = (asset: Asset, update: AssetVersionActivatedPayload): Asset => ({
  ...asset,
  ...(update.variants ? { variants: mergeVariantsByKey(asset.variants, update.variants) } : {}),
  ...(update.type ? { type: update.type } : {}),
});

export const mergeVariantsIntoSingleCache = (queryClient: QueryClient, update: AssetVersionActivatedPayload): void => {
  queryClient.setQueryData<Asset>([QueryKey.Asset, update.id], (old) =>
    old ? applyVersionActivated(old, update) : old,
  );
};

export const mergeVariantsIntoListCaches = (queryClient: QueryClient, update: AssetVersionActivatedPayload): void => {
  queryClient.setQueriesData<GetAssetsRoot>({ queryKey: [QueryKey.AssetData] }, (oldData) => {
    if (!oldData?.assets?.assets) {
      return oldData;
    }
    return {
      ...oldData,
      assets: {
        ...oldData.assets,
        assets: oldData.assets.assets.map((asset) =>
          asset.id === update.id ? applyVersionActivated(asset, update) : asset,
        ),
      },
    };
  });
};
